import React, { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { openFile, fmtSize } from "@/lib/workspace";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const MAX = 25 * 1024 * 1024;
export const ACCEPT = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt";

export function useFilePicker(onPick, testId = "file-input") {
  const ref = useRef(null);
  const input = <input ref={ref} type="file" className="hidden" accept={ACCEPT} data-testid={testId}
    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }} />;
  return [input, () => ref.current?.click()];
}

export function EvidencePanel({ a, canEdit, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (file.size > MAX) return toast.error("Files must be 25 MB or smaller.");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try { await api.post(`/workspace/actions/${a.id}/evidence`, fd); toast.success("Evidence attached"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const [input, pick] = useFilePicker(upload);
  const remove = async (f) => {
    try { await api.delete(`/workspace/actions/${a.id}/evidence/${f.id}`); toast.success("Removed"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const files = a.evidence || [];
  return (
    <div data-testid="evidence-panel">
      <div className="mb-2 flex items-center justify-between">
        <div className="label-eyebrow">Evidence</div>
        {canEdit && (
          <Button size="sm" variant="outline" className="h-8" onClick={pick} disabled={uploading} data-testid="upload-evidence-btn">
            {uploading ? <Icons.Loader2 size={14} className="animate-spin" /> : <><Icons.Upload size={14} className="mr-1.5" /> Attach file</>}
          </Button>
        )}
        {input}
      </div>
      {files.length === 0 ? (
        <p className="text-sm text-[#1E2D44]/40">No evidence attached. PDF, images and Office documents up to 25 MB.</p>
      ) : (
        <div className="space-y-1.5">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 rounded-lg border border-[#E4E8EF] px-3 py-2" data-testid={`evidence-${f.id}`}>
              <Icons.FileText size={16} className="shrink-0 text-[#B87F1E]" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-[#0B1729]">{f.original_filename}</div>
                <div className="text-[11px] text-[#1E2D44]/45">{fmtSize(f.size)} · {f.uploaded_by_name} · {fmtDateTime(f.created_at)}</div>
              </div>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openFile(f).catch(() => toast.error("You can't open this file."))} data-testid={`download-evidence-${f.id}`}>
                <Icons.Download size={15} />
              </Button>
              {canEdit && (
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#C95555]" onClick={() => remove(f)} data-testid={`remove-evidence-${f.id}`}>
                  <Icons.Trash2 size={15} />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VERB = {
  created: ["Plus", "Created"], updated: ["Pencil", "Edited"], progress: ["TrendingUp", "Progress"],
  blocker_raised: ["OctagonAlert", "Blocker raised"], blocker_cleared: ["CircleCheck", "Blocker cleared"],
  submitted: ["Send", "Submitted for review"], completed: ["CheckCircle2", "Completed"],
  review_approved: ["ShieldCheck", "Approved"], review_returned: ["Undo2", "Returned"],
  evidence_added: ["Paperclip", "Evidence added"], evidence_removed: ["Trash2", "Evidence removed"], linked: ["Link2", "Linked"],
};

export function HistoryTimeline({ events = [] }) {
  return (
    <div data-testid="history-timeline">
      <div className="label-eyebrow mb-2">History</div>
      {events.length === 0 ? <p className="text-sm text-[#1E2D44]/40">No history yet.</p> : (
        <ol className="relative space-y-3 border-l border-[#E4E8EF] pl-5">
          {events.map((e) => {
            const [icon, label] = VERB[e.verb] || ["Dot", e.verb];
            const I = Icons[icon] || Icons.Dot;
            return (
              <li key={e.id} className="relative">
                <span className="absolute -left-[29px] top-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#E4E8EF] bg-white text-[#B87F1E]"><I size={10} /></span>
                <div className="text-sm text-[#0B1729]"><b className="font-semibold">{label}</b> · {e.actor_name}</div>
                {e.detail && <div className="text-xs text-[#1E2D44]/60">{e.detail}</div>}
                <div className="text-[10px] text-[#1E2D44]/40">{fmtDateTime(e.created_at)}</div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
